// Rate-limited, latest-value-wins sender for simulated-gradient frames.
//
// The write function, the clock and the timer functions are injected so this
// sequencing can be unit-tested without Bluetooth and without fake timers. On
// the previous branch the equivalent logic lived inline in the hook, had no
// coverage, and that is precisely where its last important defect was found.
//
// Latest-value-wins is the right rule here: if the rider taps +1% five times,
// only the final gradient matters. Intermediate values would just burn
// rate-limit windows and arrive after the rider has already moved on.

export function createGradientSender({
  write,
  minIntervalMs,
  now = () => Date.now(),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) {
  // null means "nothing queued". 0 is a real gradient and must not be confused
  // with absence, so every check below is against null explicitly.
  let desired = null;
  let lastSentAt = -Infinity;
  let timerId = null;
  let sending = false;

  const schedule = () => {
    if (timerId !== null || sending) return;
    const wait = Math.max(0, minIntervalMs - (now() - lastSentAt));
    timerId = setTimer(flush, wait);
  };

  const flush = () => {
    timerId = null;
    if (sending || desired === null) return;

    const value = desired;
    desired = null;
    sending = true;

    Promise.resolve()
      .then(() => write(value))
      .catch((e) => {
        // A failed frame is not retried here: the rider's next tap re-sends it,
        // and an automatic retry loop could fight the hook's own recovery paths.
        console.warn("FTMS: gradient write failed", e);
      })
      .then(() => {
        sending = false;
        lastSentAt = now();
        if (desired !== null) schedule();
      });
  };

  return {
    send(value) {
      desired = value;
      schedule();
    },
    cancel() {
      if (timerId !== null) {
        clearTimer(timerId);
        timerId = null;
      }
      desired = null;
    },
    pending() {
      return desired;
    },
  };
}
